import { SeoMeta } from '@/types';
import { Head, usePage } from '@inertiajs/react';

/**
 * Renders the page's metadata into <head>.
 *
 * The `seo` prop is shared for every public route by HandleInertiaRequests and
 * overridden by pages backed by a model, so this component is mounted once in
 * AppLayout rather than repeated on each page.
 *
 * `head-key` matters: without it Inertia appends a second tag on navigation
 * instead of replacing the existing one.
 */
const Seo = () => {
  const { seo } = usePage<{ seo: SeoMeta | null }>().props;

  if (!seo) return null;

  return (
    <Head title={seo.title}>
      <meta
        head-key="description"
        name="description"
        content={seo.description}
      />
      <link head-key="canonical" rel="canonical" href={seo.url} />

      {seo.noindex && (
        <meta head-key="robots" name="robots" content="noindex, nofollow" />
      )}

      <meta head-key="og:type" property="og:type" content="website" />
      <meta head-key="og:url" property="og:url" content={seo.url} />
      <meta head-key="og:title" property="og:title" content={seo.og_title} />
      <meta
        head-key="og:description"
        property="og:description"
        content={seo.og_description}
      />
      {seo.image && (
        <meta head-key="og:image" property="og:image" content={seo.image} />
      )}

      <meta
        head-key="twitter:card"
        property="twitter:card"
        content="summary_large_image"
      />
      <meta
        head-key="twitter:title"
        property="twitter:title"
        content={seo.og_title}
      />
      <meta
        head-key="twitter:description"
        property="twitter:description"
        content={seo.og_description}
      />
      {seo.image && (
        <meta
          head-key="twitter:image"
          property="twitter:image"
          content={seo.image}
        />
      )}

      {seo.jsonld.length > 0 && (
        <script
          head-key="jsonld"
          type="application/ld+json"
          // Structured data is built server-side in SeoService from our own
          // models; JSON.stringify escapes it for embedding in a script tag.
          dangerouslySetInnerHTML={{ __html: JSON.stringify(seo.jsonld) }}
        />
      )}
    </Head>
  );
};

export default Seo;
